import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Chip, 
  CircularProgress,
  Typography,
  Link
} from '@mui/material';
import { ThumbUp, BookmarkBorder, ContentCopy, Search, SearchOff, Download, Description } from '@mui/icons-material';
import { submitResearchQuery, getResearchHistory, downloadSourceDocument, performDownload } from '@api/api';
import {
  Container,
  SearchBox,
  SearchForm,
  SearchInput,
  SearchButton,
  FilterSection,
  ResultTabs,
  CustomTab,
  ResultsContainer,
  ResultCard,
  ResultTitle,
  ResultMeta,
  ResultExcerpt,
  ResultActions,
  RelevanceBadge,
  FullContentModal,
  ModalContent,
  ResultTypeIcon,
} from './ResearchContainer.styles';
import { formatMatch } from '@utils/helpers';

export default function ResearchContainer() {
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState(['Private Documents', 'OpenLaw']);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [researchHistory, setResearchHistory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  const fetchResearchHistory = async () => {
    try {
      const history = await getResearchHistory();
      setResearchHistory(history);
    }
    catch (error) {
      setResearchHistory([]);
      console.error("Could not retrieve research history:", error)
    }
  };

  useEffect(() => {
    fetchResearchHistory();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFilterToggle = (filter) => {
    if (filters.includes(filter)) {
      setFilters(filters.filter(f => f !== filter));
    } else {
      setFilters([...filters, filter]);
    }
  };

  const handleViewFull = (result) => {
    setModalContent(result.fullContent);
    setIsModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setModalContent('');
  };

  const handleDownloadSource = async (fileId, fileName, event) => {
    event.preventDefault();
    if (downloadingFileId === fileId) return;

    setDownloadingFileId(fileId);
    try {
      const { blob } = await downloadSourceDocument(fileId);
      performDownload(blob, fileName);
    } catch (error) {
      console.error("Error downloading source document:", error);
      alert(`Failed to download file: ${error.message}`);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchResults([]);

    try {
      const response = await submitResearchQuery({ query: searchQuery });

      setSearchResults(response);
    } catch (error) {
      console.error('Error performing research:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const displayResults = (tabValue === 0)
    ? searchResults.filter(result => {
        if (filters.includes('OpenLaw') && result.type === 'OpenLaw Result') return true;
        if (filters.includes('Private Documents') && result.type === 'Private Document Result') return true;
        return false;
      })
    : [];

  return (
    <Container>
      <SearchBox>
        <SearchForm>
          <SearchInput
            fullWidth
            variant="outlined"
            placeholder="Search legal terms, cases, statutes, or specific questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <SearchButton
            variant="contained"
            onClick={handleSearch}
            disabled={isSearching || !searchQuery.trim()}
            startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <span className="material-icons"><Search /></span>}>
            {isSearching ? 'Searching...' : 'Search'}
          </SearchButton>
        </SearchForm>

        <FilterSection>
          <span style={{ marginRight: '10px', fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)' }}>
            Include results from:
          </span>
          <Chip
            label="Private Documents"
            variant={filters.includes('Private Documents') ? 'filled' : 'outlined'}
            onClick={() => handleFilterToggle('Private Documents')}
            style={filters.includes('Private Documents') ? { backgroundColor: 'var(--secondary-light)', color: 'var(--primary-main)' } : {}}
          />
          <Chip
            label="OpenLaw"
            variant={filters.includes('OpenLaw') ? 'filled' : 'outlined'}
            onClick={() => handleFilterToggle('OpenLaw')}
            style={filters.includes('OpenLaw') ? { backgroundColor: 'var(--secondary-light)', color: 'var(--primary-main)' } : {}}
          />
        </FilterSection>
      </SearchBox>

      <div>
        <ResultsContainer>
          {tabValue === 1 && researchHistory.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Your recent searches
              </p>
              {researchHistory.map((item) => (
                <Chip
                  key={item.id}
                  label={item.query}
                  onClick={() => setSearchQuery(item.query)}
                  style={{ margin: '5px', backgroundColor: 'var(--secondary-light)', color: 'var(--primary-main)' }}
                />
              ))}
            </div>
          )}

          {searchQuery && searchResults.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <p style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                Results for <strong>"{searchQuery}"</strong>
              </p>
            </div>
          )}

          {isSearching && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
              <CircularProgress style={{ color: 'var(--primary-main)' }} />
            </div>
          )}

          {!isSearching && displayResults.map((result) => (
            <ResultCard key={result.id}>
              <ResultTitle>{result.title}</ResultTitle>

              <ResultMeta>
                <span>{result.type}</span>
                <span>|</span>
                <span>{result.date}</span>
                <span>|</span>
                <span>{result.citation || result.publication}</span>
              </ResultMeta>

              {result.fullContent ? (
                <ResultExcerpt>{result.excerpt}</ResultExcerpt>
              ) : (
                <ResultExcerpt dangerouslySetInnerHTML={{ __html: result.excerpt }} />
              )}

              {result.sourceUrls && result.sourceUrls.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '5px' }}>Source:</p>
                  <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'none' }}>
                    {result.sourceUrls.map((fileId, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => handleDownloadSource(fileId, result.fileName, e)}
                          disabled={downloadingFileId === fileId}
                          style={{
                            color: 'var(--secondary-main)',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {result.fileName}
                        </Link>
                        {downloadingFileId === fileId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Download style={{ fontSize: '16px', color: 'var(--secondary-main)' }} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <ResultActions>
                <ResultTypeIcon>
                  {result.location !== 'N/A' && (
                    <img src={`/Icons/${result.location}.svg`} alt="Country flag" />
                  )}
                </ResultTypeIcon>

                <ResultTypeIcon>
                  {result.type === 'OpenLaw Result' && (
                    <img src='/Icons/openlaw-logo.png' alt="OpenLaw Logo" />
                  )}
                  {result.type === 'Private Document Result' && (
                    <Description />
                  )}
                </ResultTypeIcon>

                <RelevanceBadge>
                  <span className="material-icons" style={{ fontSize: '16px', marginRight: '4px' }}>
                    <ThumbUp />
                  </span>
                  {formatMatch(result.relevance)}% Match
                </RelevanceBadge>

                <Button size="small" style={{display: "none"}}>
                  <span className="material-icons" style={{ fontSize: '16px', marginRight: '4px' }}>
                    <BookmarkBorder />
                  </span>
                  Save
                </Button>

                {result.type !== 'Private Document Result' && (
                  <Button size="small">
                    <span className="material-icons" style={{ fontSize: '16px', marginRight: '4px' }}>
                      <ContentCopy />
                    </span>
                    Cite
                  </Button>
                )}

                <Button
                  size="small"
                  variant="contained"
                  style={{ backgroundColor: 'var(--secondary-main)' }}
                  onClick={() => handleViewFull(result)}
                >
                  View Full
                </Button>
              </ResultActions>
            </ResultCard>
          ))}

          {!isSearching && displayResults.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px', color: 'rgba(0, 0, 0, 0.6)' }}>
              <span className="material-icons" style={{ fontSize: '48px', opacity: 0.5, marginBottom: '15px' }}>
                <SearchOff />
              </span>
              <p>No results found. Try adjusting your search or filters.</p>
            </div>
          )}
        </ResultsContainer>
      </div>
      <FullContentModal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="full-content-modal-title"
        aria-describedby="full-content-modal-description"
      >
        <ModalContent>
          <Typography id="full-content-modal-title" variant="h6" component="h2">
            Full Content
          </Typography>
          <Typography id="full-content-modal-description" sx={{ mt: 2 }}>
            {modalContent}
          </Typography>
        </ModalContent>
      </FullContentModal>
    </Container>
  );
};
